import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Autocomplete, TextField } from "@mui/material";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";

import { StoppageDto } from "../../../DTOs/Stoppage/StoppageDto";
import { StoppageSubTypeDto } from "../../../DTOs/Stoppage/StoppageSubTypeDto";
import { stoppagesAjax } from "../../../ajax/StoppagesAjax";
import { DataTableFilteredRequest } from "../../../components/DataTable/TableHook";
import { OptionType } from "../../../types/OptionType";
import { ErrorResponse } from "../../../DTOs/Common/ErrorResponse";
import { RootState } from "../../../state/store";

type BreakdownCodeCellProps = {
  stoppage: StoppageDto
  handleStoppageSubTypeChange: (stoppage: StoppageDto, stoppageSubTypeId: number) => Promise<void>
};

const BreakdownCodeCell: React.FC<BreakdownCodeCellProps> = ({ stoppage, handleStoppageSubTypeChange }) => {
  const isShiftLocked = useSelector((state: RootState) => state.shift.isLocked);
  const [isSaving, setIsSaving] = useState(false);

  const { data: allSubTypes, isLoading } = useQuery({
    queryKey: ['references', 'stoppageSubTypes'],
    queryFn: async () => {
      const res = await stoppagesAjax.getStoppageSubtypes({ pageNumber: 0, pageSize: 1000 } as DataTableFilteredRequest);
      if (ErrorResponse.isErrorResponse(res)) throw new Error('Failed to load stoppage sub types');
      return res.data;
    },
    staleTime: 30 * 60 * 1000,
    gcTime: Number.POSITIVE_INFINITY,
    retry: false,
  });

  const stoppageSubTypeOptions = useMemo<OptionType<number>[]>(
    () => (allSubTypes ?? [])
      .filter((x: StoppageSubTypeDto) => x.stoppageTypeId === stoppage.stoppageTypeId)
      .map((x) => ({ value: x.stoppageSubTypeId ?? 0, label: x.stoppageSubType } as OptionType<number>)),
    [allSubTypes, stoppage.stoppageTypeId]
  );

  const isBusy = isLoading || isSaving;

  return (
    <Autocomplete
      disabled={isShiftLocked}
      size="small"
      sx={{ width: '100%' }}
      options={stoppageSubTypeOptions}
      loading={isBusy}
      getOptionLabel={(option) => option.label}
      value={stoppageSubTypeOptions.find((x) => x.value === stoppage.stoppageSubTypeId) ?? { value: stoppage.stoppageSubTypeId, label: stoppage.stoppageSubType?.stoppageSubType || '' }}
      onChange={async (_, val) => {
        if (!val || !val.value) return;

        setIsSaving(true);
        await handleStoppageSubTypeChange(stoppage, val.value);
        setIsSaving(false);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Select Stoppage Type"
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              endAdornment: (
                <>
                  {isBusy && (
                    <span className="animate-fade-in"><FontAwesomeIcon icon={faSpinner} spin /></span>
                  )}
                  {params.slotProps.input.endAdornment}
                </>
              )
            }
          }}
        />
      )}
    />
  )
}

export default BreakdownCodeCell
